import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import BottomNav from "@/components/BottomNav";
import BentoPath from "@/components/BentoPath";
import LoadingScreen from "@/components/LoadingScreen";
import CPXSurveyModal from "@/components/CPXSurveyModal";
import EarnCreditsHub from "@/components/EarnCreditsHub";
import HighlightToClip from "@/components/HighlightToClip";
import { api } from "@/lib/api";
import { toast } from "sonner";

const SCROLL_DEBOUNCE_MS = 1500;

/**
 * Course-reading view. Mounts:
 *   • The bento path content (wrapped in HighlightToClip — any text
 *     selection floats a "Clip to OAK-Pad" pill that auto-imports the
 *     highlighted snippet into the user's notes).
 *   • Auto-resume scroll bookmark — hydrates last_scroll_y from
 *     /api/progress on mount, debounced 1.5s persist on scroll.
 *   • OAK-Pad itself is mounted by App.js's OakPadGate (FAB → drawer).
 *     We listen for `oak:open-ad` so the rewarded-ad CTA can be invoked
 *     from anywhere inside the drawer without prop-drilling.
 */
export default function PathView() {
  const { pathId } = useParams();
  const [path, setPath] = useState(null);
  const [credits, setCredits] = useState(0);
  const [ad, setAd] = useState(false);
  const [resumeY, setResumeY] = useState(null);
  const restoredRef = useRef(false);
  const scrollDebounceRef = useRef(null);

  useEffect(() => {
    Promise.all([api.get(`/counselor/paths/${pathId}`), api.get("/credits/balance")])
      .then(([p, b]) => { setPath(p.data); setCredits(b.data.credits); })
      .catch(() => {});
    api.get(`/progress/${pathId}`)
      .then(({ data }) => {
        if (data?.exists && Number.isInteger(data.last_scroll_y) && data.last_scroll_y > 0) {
          setResumeY(data.last_scroll_y);
        }
      })
      .catch(() => {});
  }, [pathId]);

  // Auto-restore scroll once content has rendered. RAF wait so layout
  // settles before we jump.
  useEffect(() => {
    if (!path || !resumeY || restoredRef.current) return;
    restoredRef.current = true;
    requestAnimationFrame(() => {
      window.scrollTo({ top: resumeY, behavior: "instant" });
      toast.message("Resumed where you left off", {
        description: `${resumeY}px down · auto-restored from your last visit.`,
      });
    });
  }, [path, resumeY]);

  // Debounced scroll persistence — fire 1.5s after the user stops.
  useEffect(() => {
    if (!pathId) return;
    const onScroll = () => {
      if (scrollDebounceRef.current) clearTimeout(scrollDebounceRef.current);
      scrollDebounceRef.current = setTimeout(() => {
        const y = Math.max(0, Math.round(window.scrollY || document.documentElement.scrollTop || 0));
        // iter64 — also report fractional scroll progress so the
        // hamburger Continue-where-you-left-off card can show a precise
        // percentage. document.scrollingElement gives consistent
        // scrollHeight across browsers; clamp to [0,1] just in case.
        const doc = document.scrollingElement || document.documentElement;
        const viewport = window.innerHeight || doc.clientHeight || 1;
        const scrollable = Math.max(1, (doc.scrollHeight || 0) - viewport);
        const pct = Math.max(0, Math.min(1, y / scrollable));
        api.post("/progress", {
          path_id: pathId,
          last_step_index: 0,
          last_part: 1,
          last_difficulty_tier: path?.difficulty_tier || null,
          last_scroll_y: y,
          last_scroll_pct: pct,
        }).catch(() => {});
      }, SCROLL_DEBOUNCE_MS);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (scrollDebounceRef.current) clearTimeout(scrollDebounceRef.current);
    };
  }, [pathId, path?.difficulty_tier]);

  // Allow any sub-component (e.g. an OAK-Pad CTA) to open the AdModal
  // without prop-drilling.
  useEffect(() => {
    const onOpen = () => setAd(true);
    window.addEventListener("oak:open-ad", onOpen);
    return () => window.removeEventListener("oak:open-ad", onOpen);
  }, []);

  if (!path) return <LoadingScreen />;
  return (
    <div className="min-h-screen">
      <Header credits={credits} onOpenAd={() => setAd(true)} />
      <main className="max-w-5xl mx-auto px-6 md:px-10 py-10">
        <HighlightToClip pathId={pathId} className="block">
          <BentoPath
            path={path}
            onCreditsChange={setCredits}
            onNeedCredit={() => setAd(true)}
          />
        </HighlightToClip>
      </main>
      <Footer />
      <EarnCreditsHub open={ad} onOpenChange={setAd} onRewarded={setCredits} onUpgrade={() => window.dispatchEvent(new Event("oak:open-upgrade"))} />
      <BottomNav />
    </div>
  );
}
